import { useEffect, useMemo, useState } from "react";
import { FileText, Plus, Trash, CaretDown, CaretRight, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const FIELD_TYPES = [
    { value: "text", label: "Teks singkat" },
    { value: "textarea", label: "Teks panjang" },
    { value: "number", label: "Angka/Nilai" },
    { value: "choice", label: "Pilihan ganda" },
    { value: "checkbox", label: "Checkbox" },
    { value: "scale", label: "Skala 1-5" },
    { value: "percent", label: "Persentase 0-100%" },
];

const emptyField = () => ({ id: "", label: "", type: "text", required: false, optionsText: "" });

const MONTH_NAMES = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function formatPeriodMonth(period) {
    const match = /^(\d{4})-(\d{2})$/.exec(period || "");
    if (!match) return period || "—";
    return `${MONTH_NAMES[Number(match[2]) - 1]} ${match[1]}`;
}

function formatAnswerValue(field, value) {
    if (value == null || value === "") return "—";
    if (field.type === "checkbox") return Array.isArray(value) ? value.join(", ") : "—";
    if (field.type === "scale") return `${value}/5`;
    if (field.type === "percent") return `${value}%`;
    return value;
}

function currentMonthInput() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function ReportCardsTab() {
    const [templates, setTemplates] = useState([]);
    const [athletes, setAthletes] = useState([]);
    const [cards, setCards] = useState([]);
    const [templateForm, setTemplateForm] = useState({ title: "", description: "", class_name: "", fields: [emptyField()] });
    const [fillTemplateId, setFillTemplateId] = useState("");
    const [fillPeriod, setFillPeriod] = useState(currentMonthInput());
    const [fillClasses, setFillClasses] = useState([]);
    const [fillAnswers, setFillAnswers] = useState({});
    const [classPick, setClassPick] = useState("");
    const [openSessions, setOpenSessions] = useState({});
    const [editingTemplateId, setEditingTemplateId] = useState("");
    const [editingCard, setEditingCard] = useState(null);
    const [editCardPeriod, setEditCardPeriod] = useState("");
    const [editCardAnswers, setEditCardAnswers] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [templateRes, athleteRes, cardRes] = await Promise.all([
                api.get("/report-templates"),
                api.get("/coach/athletes"),
                api.get("/report-cards"),
            ]);
            setTemplates(templateRes.data);
            setAthletes(athleteRes.data);
            setCards(cardRes.data);
            if (!fillTemplateId && templateRes.data.length) {
                setFillTemplateId(templateRes.data[0].id);
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const setField = (index, key, value) => {
        setTemplateForm((prev) => ({
            ...prev,
            fields: prev.fields.map((field, i) => (i === index ? { ...field, [key]: value } : field)),
        }));
    };

    const saveTemplate = async () => {
        setSaving(true);
        const payload = {
            title: templateForm.title,
            description: templateForm.description,
            class_name: templateForm.class_name,
            fields: templateForm.fields.map((field) => ({
                id: field.id,
                label: field.label,
                type: field.type,
                required: field.required,
                options: field.optionsText ? field.optionsText.split(",").map((x) => x.trim()).filter(Boolean) : [],
            })),
        };
        try {
            if (editingTemplateId) {
                await api.put(`/report-templates/${editingTemplateId}`, payload);
                toast.success("Perubahan format rapot tersimpan");
            } else {
                await api.post("/report-templates", payload);
                toast.success("Format rapot tersimpan");
            }
            setEditingTemplateId("");
            setTemplateForm({ title: "", description: "", class_name: "", fields: [emptyField()] });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const startEditTemplate = (template) => {
        setEditingTemplateId(template.id);
        setTemplateForm({
            title: template.title || "",
            description: template.description || "",
            class_name: template.class_name || "",
            fields: (template.fields || []).map((field) => ({
                id: field.id,
                label: field.label,
                type: field.type,
                required: Boolean(field.required),
                optionsText: (field.options || []).join(", "),
            })),
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const cancelEditTemplate = () => {
        setEditingTemplateId("");
        setTemplateForm({ title: "", description: "", class_name: "", fields: [emptyField()] });
    };

    const openEditCard = (card) => {
        setEditingCard(card);
        setEditCardPeriod(card.period || "");
        setEditCardAnswers({ ...(card.answers || {}) });
    };

    const setCardAnswer = (field, value) => {
        setEditCardAnswers((prev) => ({ ...prev, [field.id]: value }));
    };

    const toggleCardCheckbox = (field, option) => {
        const current = editCardAnswers[field.id] || [];
        setCardAnswer(field, current.includes(option) ? current.filter((x) => x !== option) : [...current, option]);
    };

    const saveEditedCard = async () => {
        if (!editingCard) return;
        if (!editCardPeriod) {
            toast.error("Pilih periode rapot dulu");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/report-cards/${editingCard.id}`, { period: editCardPeriod, answers: editCardAnswers });
            toast.success(`Rapot ${editingCard.athlete_name} diperbarui`);
            setEditingCard(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const selectedTemplate = templates.find((template) => template.id === fillTemplateId);

    const classOptions = useMemo(() => [...new Set(athletes.map((a) => a.class_name).filter(Boolean))].sort(), [athletes]);

    const fillAthletes = useMemo(() => {
        const byClass = {};
        fillClasses.forEach((className) => {
            byClass[className] = athletes.filter((a) => a.class_name === className);
        });
        return byClass;
    }, [athletes, fillClasses]);

    const addFillClass = (className) => {
        if (!className || fillClasses.includes(className)) return;
        setFillClasses((prev) => [...prev, className]);
        setClassPick("");
    };

    const removeFillClass = (className) => {
        setFillClasses((prev) => prev.filter((c) => c !== className));
    };

    const setAnswer = (athleteId, field, value) => {
        setFillAnswers((prev) => ({ ...prev, [athleteId]: { ...(prev[athleteId] || {}), [field.id]: value } }));
    };

    const toggleCheckbox = (athleteId, field, option) => {
        const current = (fillAnswers[athleteId] || {})[field.id] || [];
        setAnswer(athleteId, field, current.includes(option) ? current.filter((x) => x !== option) : [...current, option]);
    };

    const saveCard = async () => {
        if (!selectedTemplate) {
            toast.error("Pilih format rapot dulu");
            return;
        }
        if (!fillPeriod) {
            toast.error("Pilih periode rapot dulu");
            return;
        }
        const isEmpty = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);
        const entries = [];
        Object.values(fillAthletes).flat().forEach((athlete) => {
            const answers = fillAnswers[athlete.id] || {};
            const filled = selectedTemplate.fields.some((field) => !isEmpty(answers[field.id]));
            if (filled) entries.push({ athlete_id: athlete.id, answers });
        });
        if (entries.length === 0) {
            toast.error("Isi rapot minimal satu atlet (atlet yang kosong semua otomatis dilewati)");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/report-cards/bulk", { template_id: fillTemplateId, period: fillPeriod, entries });
            toast.success(data.message || `${entries.length} rapot tersimpan`);
            setFillAnswers({});
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const cardFolders = useMemo(() => {
        const byClass = {};
        cards.forEach((card) => {
            const className = card.class_name || "Tanpa Kelas";
            (byClass[className] = byClass[className] || []).push(card);
        });
        return Object.keys(byClass).sort().map((className) => {
            const sessionMap = new Map();
            byClass[className].forEach((card) => {
                const key = `${card.period}||${card.template_id}`;
                if (!sessionMap.has(key)) {
                    sessionMap.set(key, { key, period: card.period, template_title: card.template?.title || "Rapot", coach_name: card.coach_name, updated_at: card.updated_at, cards: [] });
                }
                sessionMap.get(key).cards.push(card);
            });
            const sessions = [...sessionMap.values()];
            sessions.forEach((group) => group.cards.sort((a, b) => a.athlete_name.localeCompare(b.athlete_name)));
            sessions.sort((a, b) => (b.period || "").localeCompare(a.period || ""));
            return { className, count: byClass[className].length, sessions };
        });
    }, [cards]);

    const [openFolders, setOpenFolders] = useState({});
    const toggleFolder = (name) => setOpenFolders((prev) => ({ ...prev, [name]: !prev[name] }));
    const toggleSession = (key) => setOpenSessions((prev) => ({ ...prev, [key]: !prev[key] }));

    const removeCard = async (card) => {
        if (!window.confirm(`Hapus rapot ${card.athlete_name} (${card.template?.title || "Rapot"} • ${card.period})? Tindakan ini tidak bisa dibatalkan.`)) return;
        try {
            await api.delete(`/report-cards/${card.id}`);
            toast.success("Rapot dihapus");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const removeTemplate = async (template) => {
        if (!window.confirm(`Hapus format rapot "${template.title}"?`)) return;
        try {
            await api.delete(`/report-templates/${template.id}`);
            toast.success("Format rapot dihapus");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-10" data-testid="report-cards-tab">
            <section>
                <h2 className="font-display uppercase text-2xl">Format Rapot</h2>
                <p className="text-sm text-racing-muted mt-2">Buat format rapot sendiri seperti Google Form: teks, nilai, pilihan ganda, checkbox, dan skala 1-5.</p>
                {editingTemplateId && (
                    <p className="mt-3 inline-block border border-racing-gold bg-racing-gold/10 text-racing-golddeep px-3 py-1.5 text-[10px] uppercase tracking-[0.2em]" data-testid="template-editing-badge">
                        Sedang mengedit format — Simpan Perubahan atau Batal
                    </p>
                )}
                <div className="mt-6 bg-white border border-racing-line p-6 space-y-5" data-testid="report-template-builder">
                    <div className="grid md:grid-cols-3 gap-4">
                        <input value={templateForm.title} onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })} data-testid="template-title" className="bg-racing-surface border border-racing-line px-4 py-3 text-sm" placeholder="Judul rapot" />
                        <input value={templateForm.class_name} onChange={(e) => setTemplateForm({ ...templateForm, class_name: e.target.value })} data-testid="template-class" className="bg-racing-surface border border-racing-line px-4 py-3 text-sm" placeholder="Kelas (opsional)" />
                        <input value={templateForm.description} onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} data-testid="template-description" className="bg-racing-surface border border-racing-line px-4 py-3 text-sm" placeholder="Deskripsi" />
                    </div>
                    {templateForm.fields.map((field, index) => (
                        <div key={index} className="border border-racing-line bg-racing-surface p-4 grid md:grid-cols-5 gap-3" data-testid={`template-field-${index}`}>
                            <input value={field.label} onChange={(e) => setField(index, "label", e.target.value)} data-testid={`field-label-${index}`} className="bg-white border border-racing-line px-3 py-2 text-sm" placeholder="Label isian" />
                            <select value={field.type} onChange={(e) => setField(index, "type", e.target.value)} data-testid={`field-type-${index}`} className="bg-white border border-racing-line px-3 py-2 text-sm">{FIELD_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select>
                            <input value={field.optionsText} onChange={(e) => setField(index, "optionsText", e.target.value)} data-testid={`field-options-${index}`} className="bg-white border border-racing-line px-3 py-2 text-sm" placeholder="Opsi pisah koma" disabled={!["choice", "checkbox"].includes(field.type)} />
                            <label className="flex items-center gap-2 text-xs text-racing-muted"><input type="checkbox" checked={field.required} onChange={(e) => setField(index, "required", e.target.checked)} data-testid={`field-required-${index}`} /> Wajib</label>
                            <button type="button" onClick={() => setTemplateForm({ ...templateForm, fields: templateForm.fields.filter((_, i) => i !== index) })} data-testid={`field-delete-${index}`} className="p-2 border border-racing-line text-racing-red"><Trash size={15} /></button>
                        </div>
                    ))}
                    <div className="flex flex-wrap gap-3">
                        <button type="button" onClick={() => setTemplateForm({ ...templateForm, fields: [...templateForm.fields, emptyField()] })} data-testid="add-template-field" className="border border-racing-red text-racing-red px-4 py-2 text-xs uppercase tracking-[0.2em]"><Plus size={14} className="inline mr-1" /> Tambah Isian</button>
                        <button type="button" onClick={saveTemplate} disabled={saving} data-testid="save-template" className="bg-racing-red text-white px-5 py-2 text-xs uppercase tracking-[0.2em]">{editingTemplateId ? "Simpan Perubahan" : "Simpan Format"}</button>
                        {editingTemplateId && (
                            <button type="button" onClick={cancelEditTemplate} data-testid="cancel-edit-template" className="border border-racing-line text-racing-muted px-5 py-2 text-xs uppercase tracking-[0.2em] hover:text-racing-red">Batal</button>
                        )}
                    </div>
                </div>
                <div className="mt-5 grid md:grid-cols-3 gap-4">
                    {templates.map((template, index) => (
                        <div key={template.id} className="bg-white border border-racing-line p-5" data-testid={`template-card-${index}`}>
                            <p className="font-display uppercase text-xl">{template.title}</p>
                            <p className="text-xs text-racing-muted mt-2">{template.fields.length} isian • {template.class_name || "Semua kelas"}</p>
                            <div className="mt-4 flex gap-2">
                                <button onClick={() => startEditTemplate(template)} data-testid={`template-edit-${index}`} className="p-2 border border-racing-line text-racing-muted hover:text-neutral-900 hover:border-black/40 transition-colors" aria-label="Edit format"><PencilSimple size={15} /></button>
                                <button onClick={() => removeTemplate(template)} data-testid={`template-delete-${index}`} className="p-2 border border-racing-line text-racing-red"><Trash size={15} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-white border border-racing-line p-6" data-testid="report-fill-section">
                <h2 className="font-display uppercase text-2xl mb-5">Isi Rapot Siswa</h2>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <select value={fillTemplateId} onChange={(e) => { setFillTemplateId(e.target.value); setFillAnswers({}); }} data-testid="report-template-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm">
                        <option value="" disabled>Pilih format rapot</option>
                        {templates.map((template) => <option key={template.id} value={template.id}>{template.title}</option>)}
                    </select>
                    <div>
                        <input type="month" value={fillPeriod} onChange={(e) => setFillPeriod(e.target.value)} data-testid="report-period-input" className="w-full bg-racing-surface border border-racing-line px-3 py-3 text-sm" />
                        <p className="text-[10px] text-racing-muted mt-1">Periode rapot dalam format bulan-tahun{fillPeriod ? ` • ${formatPeriodMonth(fillPeriod)}` : ""}</p>
                    </div>
                    <select value={classPick} onChange={(e) => addFillClass(e.target.value)} data-testid="report-class-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm self-start">
                        <option value="">Pilih kelas atlet...</option>
                        {classOptions.filter((c) => !fillClasses.includes(c)).map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                </div>
                {selectedTemplate && fillClasses.length === 0 && (
                    <p className="text-sm text-racing-muted border border-dashed border-racing-line px-4 py-6 text-center" data-testid="report-fill-empty">Pilih kelas untuk menampilkan nama-nama atletnya. Anda bisa menambahkan atlet dari kelas lain dengan memilih kelas berikutnya.</p>
                )}
                {selectedTemplate && fillClasses.map((className) => (
                    <div key={className} className="mb-8 border border-racing-line" data-testid={`report-class-block-${classOptions.indexOf(className)}`}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-racing-line bg-racing-surface">
                            <p className="font-display uppercase text-lg">{className} <span className="text-xs text-racing-muted font-sans normal-case tracking-normal">({(fillAthletes[className] || []).length} atlet)</span></p>
                            <button type="button" onClick={() => removeFillClass(className)} data-testid={`report-class-remove-${classOptions.indexOf(className)}`} className="text-racing-muted hover:text-racing-red transition-colors" aria-label={`Hapus kelas ${className}`}><Trash size={15} /></button>
                        </div>
                        {(fillAthletes[className] || []).map((athlete, athleteIndex) => (
                            <div key={athlete.id} className="px-4 py-5 border-b border-racing-line last:border-0 space-y-5" data-testid={`report-athlete-block-${athleteIndex}`}>
                                <p className="font-medium text-neutral-900">{athlete.name}</p>
                                {selectedTemplate.fields.map((field, fieldIndex) => {
                                    const answers = fillAnswers[athlete.id] || {};
                                    const prefix = `report-a${athleteIndex}-f${fieldIndex}`;
                                    return (
                                        <div key={field.id} data-testid={`report-field-${athleteIndex}-${fieldIndex}`}>
                                            <label className="block text-xs uppercase tracking-[0.2em] text-racing-muted mb-2">{field.label}{field.required ? " *" : ""}</label>
                                            {field.type === "textarea" ? <textarea rows={3} value={answers[field.id] || ""} onChange={(e) => setAnswer(athlete.id, field, e.target.value)} data-testid={`${prefix}-text`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm" /> : null}
                                            {field.type === "text" ? <input value={answers[field.id] || ""} onChange={(e) => setAnswer(athlete.id, field, e.target.value)} data-testid={`${prefix}-text`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm" /> : null}
                                            {field.type === "number" ? <input type="number" value={answers[field.id] || ""} onChange={(e) => setAnswer(athlete.id, field, e.target.value)} data-testid={`${prefix}-number`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm" /> : null}
                                            {field.type === "choice" ? <select value={answers[field.id] || ""} onChange={(e) => setAnswer(athlete.id, field, e.target.value)} data-testid={`${prefix}-choice`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm"><option value="">Pilih</option>{field.options.map((option) => <option key={option}>{option}</option>)}</select> : null}
                                            {field.type === "checkbox" ? <div className="flex flex-wrap gap-3">{field.options.map((option) => <label key={option} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={(answers[field.id] || []).includes(option)} onChange={() => toggleCheckbox(athlete.id, field, option)} data-testid={`${prefix}-check-${option}`} />{option}</label>)}</div> : null}
                                            {field.type === "scale" ? <div className="flex gap-2">{[1, 2, 3, 4, 5].map((value) => <button type="button" key={value} onClick={() => setAnswer(athlete.id, field, value)} data-testid={`${prefix}-scale-${value}`} className={`w-10 h-10 border ${answers[field.id] === value ? "bg-racing-red text-white border-racing-red" : "border-racing-line"}`}>{value}</button>)}</div> : null}
                                            {field.type === "percent" ? (
                                                <div data-testid={`${prefix}-percent`}>
                                                    <div className="flex items-center gap-4">
                                                        <input type="range" min="0" max="100" step="1" value={answers[field.id] ?? 50} onChange={(e) => setAnswer(athlete.id, field, Number(e.target.value))} data-testid={`${prefix}-slider`} className="flex-1 h-2 accent-racing-red cursor-pointer" />
                                                        <span className="font-display text-2xl text-racing-red w-16 text-right" data-testid={`${prefix}-percent-value`}>{answers[field.id] ?? 50}%</span>
                                                    </div>
                                                    <div className="mt-2 h-3 bg-racing-surface border border-racing-line" data-testid={`${prefix}-percent-bar`}>
                                                        <div className="h-full bg-racing-red transition-[width] duration-200" style={{ width: `${answers[field.id] ?? 50}%` }} />
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                ))}
                {selectedTemplate && fillClasses.length > 0 && (
                    <button onClick={saveCard} disabled={saving} data-testid="save-report-card" className="bg-racing-red text-white px-6 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan Rapot"}</button>
                )}
            </section>

            <section>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
                    <h2 className="font-display uppercase text-2xl">Rapot Tersimpan</h2>
                    <div className="flex gap-2">
                        <a href={`${api.defaults.baseURL}/report-cards/export?period=${encodeURIComponent(fillPeriod)}&format=xlsx`} data-testid="report-export-xlsx" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">Excel</a>
                        <a href={`${api.defaults.baseURL}/report-cards/export?period=${encodeURIComponent(fillPeriod)}&format=pdf`} data-testid="report-export-pdf" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">PDF</a>
                    </div>
                </div>
                <div className="space-y-4" data-testid="report-sessions">
                    {cardFolders.length === 0 && <p className="text-racing-muted" data-testid="report-empty">Belum ada rapot tersimpan.</p>}
                    {cardFolders.map((folder, folderIndex) => {
                        const folderOpen = Boolean(openFolders[folder.className]);
                        return (
                            <div key={folder.className} className="border border-racing-line bg-white" data-testid={`report-folder-${folderIndex}`}>
                                <button
                                    type="button"
                                    onClick={() => toggleFolder(folder.className)}
                                    data-testid={`report-folder-toggle-${folderIndex}`}
                                    className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-black/[0.03] transition-colors"
                                >
                                    <span className="text-racing-muted">{folderOpen ? <CaretDown size={18} /> : <CaretRight size={18} />}</span>
                                    <span className="flex-1 font-display uppercase text-xl text-neutral-900">{folder.className}</span>
                                    <span className="text-xs text-racing-muted" data-testid={`report-folder-count-${folderIndex}`}>{folder.count} rapot • {folder.sessions.length} sesi</span>
                                </button>
                                {folderOpen && (
                                    <div className="border-t border-racing-line">
                                        {folder.sessions.map((group, sessionIndex) => {
                                            const sessionKey = `${folder.className}||${group.key}`;
                                            const isOpen = Boolean(openSessions[sessionKey]);
                                            return (
                                                <div key={sessionKey} className="border-b border-racing-line last:border-0" data-testid={`report-session-${folderIndex}-${sessionIndex}`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleSession(sessionKey)}
                                                        data-testid={`report-session-toggle-${folderIndex}-${sessionIndex}`}
                                                        className="w-full flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3.5 text-left bg-racing-surface/60 hover:bg-racing-surface transition-colors"
                                                    >
                                                        <span className="text-racing-muted">{isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}</span>
                                                        <span className="min-w-[150px]">
                                                            <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted">Periode</span>
                                                            <span className="block font-medium text-neutral-900" data-testid={`report-session-period-${folderIndex}-${sessionIndex}`}>{formatPeriodMonth(group.period)}</span>
                                                        </span>
                                                        <span className="flex-1 min-w-[160px]">
                                                            <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted">Format Rapot</span>
                                                            <span className="block font-medium text-neutral-900">{group.template_title}</span>
                                                        </span>
                                                        <span>
                                                            <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted">Atlet</span>
                                                            <span className="block font-display text-lg text-neutral-900" data-testid={`report-session-count-${folderIndex}-${sessionIndex}`}>{group.cards.length}</span>
                                                        </span>
                                                        <span>
                                                            <span className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted">Pelatih</span>
                                                            <span className="block text-sm text-neutral-900" data-testid={`report-session-coach-${folderIndex}-${sessionIndex}`}>{group.coach_name || "—"}</span>
                                                        </span>
                                                    </button>
                                                    {isOpen && (
                                                        <div className="border-t border-racing-line" data-testid={`report-session-detail-${folderIndex}-${sessionIndex}`}>
                                                            {group.cards.map((card, cardIndex) => (
                                                                <div key={card.id} className="px-5 py-4 border-b border-racing-line last:border-0" data-testid={`report-card-${folderIndex}-${sessionIndex}-${cardIndex}`}>
                                                                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                                                                        <p className="flex items-center gap-2 font-medium text-neutral-900"><FileText size={16} className="text-racing-golddeep" /> {card.athlete_name}</p>
                                                                        <span className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">{card.class_name || "—"}</span>
                                                                        <span className="ml-auto flex gap-2">
                                                                            <button type="button" onClick={() => openEditCard(card)} data-testid={`report-card-edit-${folderIndex}-${sessionIndex}-${cardIndex}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red text-[10px] uppercase tracking-[0.15em] transition-colors">
                                                                                <PencilSimple size={13} /> Edit
                                                                            </button>
                                                                            <button type="button" onClick={() => removeCard(card)} data-testid={`report-card-delete-${folderIndex}-${sessionIndex}-${cardIndex}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red text-[10px] uppercase tracking-[0.15em] transition-colors" aria-label="Hapus rapot">
                                                                                <Trash size={13} /> Hapus
                                                                            </button>
                                                                        </span>
                                                                    </div>
                                                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                                        {(card.template?.fields || []).map((field) => {
                                                                            const value = card.answers?.[field.id];
                                                                            if (value == null || value === "") return null;
                                                                            return (
                                                                                <div key={field.id} className="bg-racing-surface border border-racing-line px-3 py-2">
                                                                                    <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">{field.label}</p>
                                                                                    {field.type === "percent" ? (
                                                                                        <div className="flex items-center gap-2 mt-1">
                                                                                            <div className="flex-1 h-2 bg-white border border-racing-line"><div className="h-full bg-racing-red" style={{ width: `${Math.min(100, Math.max(0, Number(value) || 0))}%` }} /></div>
                                                                                            <span className="text-sm font-medium text-racing-red">{value}%</span>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <p className="text-sm text-neutral-900 mt-1 whitespace-pre-line">{formatAnswerValue(field, value)}</p>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            <Dialog open={Boolean(editingCard)} onOpenChange={(open) => { if (!open) setEditingCard(null); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="report-edit-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-wide text-xl">Edit Rapot — {editingCard?.athlete_name}</DialogTitle>
                        <DialogDescription className="sr-only">Ubah periode dan isian rapot siswa yang sudah tersimpan.</DialogDescription>
                    </DialogHeader>
                    {editingCard && (
                        <div className="space-y-5 mt-2">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Periode (bulan-tahun)</label>
                                <input type="month" value={/^\d{4}-\d{2}$/.test(editCardPeriod) ? editCardPeriod : ""} onChange={(e) => setEditCardPeriod(e.target.value)} data-testid="report-edit-period" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                                {!/^\d{4}-\d{2}$/.test(editCardPeriod) && editCardPeriod && <p className="text-[10px] text-racing-golddeep mt-1">Periode lama "{editCardPeriod}" bukan format bulan-tahun — pilih bulan baru untuk memperbaruinya.</p>}
                            </div>
                            {(editingCard.template?.fields || []).map((field, fieldIndex) => {
                                const prefix = `report-edit-f${fieldIndex}`;
                                return (
                                    <div key={field.id} data-testid={`report-edit-field-${fieldIndex}`}>
                                        <label className="block text-xs uppercase tracking-[0.2em] text-racing-muted mb-2">{field.label}{field.required ? " *" : ""}</label>
                                        {field.type === "textarea" ? <textarea rows={3} value={editCardAnswers[field.id] || ""} onChange={(e) => setCardAnswer(field, e.target.value)} data-testid={`${prefix}-text`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm" /> : null}
                                        {field.type === "text" ? <input value={editCardAnswers[field.id] || ""} onChange={(e) => setCardAnswer(field, e.target.value)} data-testid={`${prefix}-text`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm" /> : null}
                                        {field.type === "number" ? <input type="number" value={editCardAnswers[field.id] ?? ""} onChange={(e) => setCardAnswer(field, e.target.value)} data-testid={`${prefix}-number`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm" /> : null}
                                        {field.type === "choice" ? <select value={editCardAnswers[field.id] || ""} onChange={(e) => setCardAnswer(field, e.target.value)} data-testid={`${prefix}-choice`} className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm"><option value="">Pilih</option>{field.options.map((option) => <option key={option}>{option}</option>)}</select> : null}
                                        {field.type === "checkbox" ? <div className="flex flex-wrap gap-3">{field.options.map((option) => <label key={option} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={(editCardAnswers[field.id] || []).includes(option)} onChange={() => toggleCardCheckbox(field, option)} data-testid={`${prefix}-check-${option}`} />{option}</label>)}</div> : null}
                                        {field.type === "scale" ? <div className="flex gap-2">{[1, 2, 3, 4, 5].map((value) => <button type="button" key={value} onClick={() => setCardAnswer(field, value)} data-testid={`${prefix}-scale-${value}`} className={`w-10 h-10 border ${editCardAnswers[field.id] === value ? "bg-racing-red text-white border-racing-red" : "border-racing-line"}`}>{value}</button>)}</div> : null}
                                        {field.type === "percent" ? (
                                            <div data-testid={`${prefix}-percent`}>
                                                <div className="flex items-center gap-4">
                                                    <input type="range" min="0" max="100" step="1" value={editCardAnswers[field.id] ?? 50} onChange={(e) => setCardAnswer(field, Number(e.target.value))} data-testid={`${prefix}-slider`} className="flex-1 h-2 accent-racing-red cursor-pointer" />
                                                    <span className="font-display text-2xl text-racing-red w-16 text-right" data-testid={`${prefix}-percent-value`}>{editCardAnswers[field.id] ?? 50}%</span>
                                                </div>
                                                <div className="mt-2 h-3 bg-racing-surface border border-racing-line">
                                                    <div className="h-full bg-racing-red transition-[width] duration-200" style={{ width: `${editCardAnswers[field.id] ?? 50}%` }} />
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                            <button onClick={saveEditedCard} disabled={saving} data-testid="report-edit-save" className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors">
                                {saving ? "Menyimpan..." : "Simpan Perubahan Rapot"}
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
